import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  TablePagination,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Divider,
  Box,
  Typography,
  Alert,
  LinearProgress,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { IconPlus, IconFileSpreadsheet, IconDownload, IconUpload, IconCloudUpload } from "@tabler/icons-react";
import MainCard from "ui-component/cards/MainCard";
import SearchSection from "layout/MainLayout/Header/SearchSection";
import { AddButton } from "components/ui/buttons/StyledButtons";
import ContractsTable from "components/contracts/ContractsTable";
import ContractForm from "components/contracts/ContractForm";
import ContractStatusHistoryModal from "components/contracts/ContractStatusHistoryModal";
import ContractsFilters from "components/contracts/ContractsFilters";
import ContractFileModal from "components/contracts/ContractFileModal";
import ContractDownloadPackModal from "components/contracts/ContractDownloadPackModal";
import { useContractEditor } from "components/contracts/ContractEditorContext";
import useContractPermissions from "hooks/useContractPermissions";
import useDragScroll from "hooks/useDragScroll";
import {
  calculateDaysDifference,
  calculateInstallmentQuestionedValue,
  calculatePgto,
  calculateYearlyInterest,
} from "utils/contract-calculations";
import {
  allContractStatuses,
  contractCategories,
} from "utils/contract-editor";
import {
  EMPTY_CONTRACT_FILTERS,
} from "utils/contract-filters";
import { formatDocument } from "utils/format-document";
import {
  confirmGenerateDespiteNonAbusiveRate,
} from "utils/rate-abusiveness";

const ContractsList = () => {
  const { permissions, allowedStatuses, loading: permissionsLoading, hasSingleStatus, singleStatus, canRestore, canImportData, isAdmin } = useContractPermissions();
  const { openNewContract, openContract, getCurrentContract, preloadContractCatalogs } = useContractEditor();
  const defaultFilterAppliedRef = useRef(false);
  const fileInputRef = React.useRef(null);
  const {
    ref: tableScrollRef,
    isDragging: isTableDragging,
    isSelectionMode: isTableSelectionMode,
    onMouseDown: handleTableScrollMouseDown,
  } = useDragScroll({ verticalScroll: "window" });
  const [customers, setCustomers] = useState([]);
  const [banks, setBanks] = useState([]);
  const [contractTypes, setContractTypes] = useState([]);
  const [lawyers, setLawyers] = useState([]);
  const [salesConsultants, setSalesConsultants] = useState([]);
  const [users, setUsers] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [page, setPage] = useState(0);
  const [openHistoryModal, setOpenHistoryModal] = useState(false);
  const [selectedContractForHistory, setSelectedContractForHistory] = useState(null);
  const [openFileModal, setOpenFileModal] = useState(false);
  const [selectedContractForFile, setSelectedContractForFile] = useState(null);
  const [openDownloadPackModal, setOpenDownloadPackModal] = useState(false);
  const [selectedContractForDownloadPack, setSelectedContractForDownloadPack] = useState(null);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalCustomers, setTotalCustomers] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [importedStatusFilter, setImportedStatusFilter] = useState("all");
  const [missingDataFilter, setMissingDataFilter] = useState("all");
  const [showDeletedContracts, setShowDeletedContracts] = useState(false);
  const [initialFiltersLoaded, setInitialFiltersLoaded] = useState(false);
  const [filters, setFilters] = useState({ ...EMPTY_CONTRACT_FILTERS });
  const [statusFilterApplied, setStatusFilterApplied] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isSyncingSheets, setIsSyncingSheets] = useState(false);
  const [openImportModal, setOpenImportModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [deleteImportedBefore, setDeleteImportedBefore] = useState(false);
  const [importMode, setImportMode] = useState("processos");
  const [selectionEnabled, setSelectionEnabled] = useState(false);
  const [selectedContractIds, setSelectedContractIds] = useState([]);
  const [openBulkEditModal, setOpenBulkEditModal] = useState(false);
  const [bulkEditSubmitting, setBulkEditSubmitting] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkRestoring, setBulkRestoring] = useState(false);
  const [bulkTabIndex, setBulkTabIndex] = useState(0);
  const [bulkContract, setBulkContract] = useState({
    number: "",
    date: "",
    category: "",
    status: "",
    customer: null,
    bank: null,
    lawyer: null,
    lawyer_wallet: null,
    service_obs: "",
    process_protocol_obs: "",
    filed_at: "",
    file_at: "",
    state: "",
    process_number: "",
    complaint_protocol_number: "",
    sales_consultant: null,
    contracted_value: null,
    installments: null,
    installment_value: null,
    contract_expenses_value: null,
    installment_part_not_questioned_value: null,
    installment_questioned_value: null,
    interest_rate_monthly_value: null,
    interest_rate_yearly_value: null,
    contract_at: "",
    first_installment_at: "",
    grace_period_days: null,
    installments_payed: null,
    is_renegotiation: null,
    renegotiation_installments: null,
    renegotiation_value: null,
    contract_type: null,
    interest_rate_average_monthly_value: null,
    interest_rate_average_yearly_value: null,
    updated_at: "",
    contract_installments: [],
    total_amortization: null,
    total_installment_value: null,
    total_overpaid_value: null,
    total_legal_interest: null,
    total_correction_value: null,
    responsible_user: null,
    // tri-state: null = não alterar; true/false = aplicar
    has_ajg: null,
    has_after_sales_video: null,
    declares_irpf: null,
  });

  // Lista de status filtrada pelo perfil do usuário (para uso nos filtros)
  const contractStatuses = allowedStatuses 
    ? allContractStatuses.filter(status => allowedStatuses.includes(status.value))
    : allContractStatuses;

  const refreshListRef = useRef(null);

  const applyFiltersSnapshot = useCallback((parsed) => {
    setFilters(parsed.filters);
    setSearchTerm(parsed.searchTerm);
    setImportedStatusFilter(parsed.importedStatusFilter);
    setMissingDataFilter(parsed.missingDataFilter);
    setShowDeletedContracts(parsed.showDeletedContracts);
    setPage(0);
  }, []);

  const handleDefaultFilterLoaded = useCallback(() => {
    defaultFilterAppliedRef.current = true;
    setStatusFilterApplied(true);
  }, []);

  const handleInitialFiltersReady = useCallback(() => {
    setInitialFiltersLoaded(true);
  }, []);

  const fetchContracts = useCallback(() => {
    const params = new URLSearchParams();
    params.append("page", page + 1);
    params.append("page_size", rowsPerPage);
    
    // Adiciona busca geral se houver
    if (searchTerm !== "") {
      params.append("contains", searchTerm);
    }

    // Filtro por status de importação
    if (importedStatusFilter !== "all") {
      params.append("imported_status", importedStatusFilter);
    }

    // Filtro por contratos com dados faltantes
    if (missingDataFilter === "yes") {
      params.append("missing_data", "1");
    }
    
    // Adiciona filtros avançados
    Object.keys(filters).forEach((key) => {
      const value = filters[key];
      if (value !== null && value !== "" && value !== undefined) {
        params.append(key, value);
      }
    });
    
    const endpoint = showDeletedContracts
      ? `/backend/contracts/deleted/?${params.toString()}`
      : `/backend/contracts/?${params.toString()}`;

    return fetch(endpoint)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Falha ao carregar os contratos (status ${response.status})`);
        }
        return response.json();
      })
      .then((data) => {
        setContracts(data.results);
        setTotalRecords(data.count);
        setTotalCustomers(data.customers_count ?? 0);
      });
  }, [searchTerm, page, rowsPerPage, filters, importedStatusFilter, missingDataFilter, showDeletedContracts]);

  const fetchCustomers = useCallback(async () => {
    try {
      const response = await fetch("/backend/customers/");
      const data = await response.json();
      setCustomers(data.results);
    } catch (error) {
      console.error("Erro ao buscar clientes:", error);
    }
  }, []);

  const fetchBanks = useCallback(async () => {
    try {
      const response = await fetch("/backend/banks/?page_size=1000");
      const data = await response.json();
      setBanks(data.results);
    } catch (error) {
      console.error("Erro ao buscar bancos:", error);
    }
  }, []);

  const fetchLawyers = useCallback(async () => {
    try {
      const response = await fetch("/backend/lawyers/?page_size=1000");
      const data = await response.json();
      setLawyers(data.results);
    } catch (error) {
      console.error("Erro ao buscar advogados:", error);
    }
  }, []);

  const fetchSalesConsultants = useCallback(async () => {
    try {
      const response = await fetch("/backend/sales_consultants/?page_size=1000");
      const data = await response.json();
      setSalesConsultants(data.results);
    } catch (error) {
      console.error("Erro ao buscar consultores de vendas:", error);
    }
  }, []);

  const fetchContractTypes = useCallback(async () => {
    try {
      // Buscar apenas tipos de contrato ativos para uso em contratos
      const response = await fetch("/backend/contract-types/?active=true");
      const data = await response.json();
      setContractTypes(data.results);
    } catch (error) {
      console.error("Erro ao buscar tipos de contrato:", error);
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await fetch("/backend/users-list/");
      const data = await response.json();
      setUsers(Array.isArray(data) ? data : (data.results || []));
    } catch (error) {
      console.error("Erro ao buscar usuários:", error);
    }
  }, []);

  useEffect(() => {
    fetchCustomers();
    fetchBanks();
    fetchContractTypes();
    fetchLawyers();
    fetchSalesConsultants();
    fetchUsers();
  }, [
    fetchCustomers,
    fetchBanks,
    fetchLawyers,
    fetchSalesConsultants,
    fetchContractTypes,
    fetchUsers,
  ]);

  useEffect(() => {
    preloadContractCatalogs();
  }, [preloadContractCatalogs]);

  useEffect(() => {
    if (!initialFiltersLoaded) return;
    fetchContracts();
  }, [fetchContracts, initialFiltersLoaded, page, rowsPerPage, searchTerm, filters]);

  useEffect(() => {
    if (!selectionEnabled) {
      setSelectedContractIds([]);
    }
  }, [selectionEnabled]);

  // Aplica o filtro automático quando há apenas um status permitido no perfil
  useEffect(() => {
    if (!initialFiltersLoaded || permissionsLoading) return;
    if (defaultFilterAppliedRef.current) return;
    if (hasSingleStatus && singleStatus && !statusFilterApplied) {
      setFilters(prev => ({ ...prev, status: singleStatus }));
      setStatusFilterApplied(true);
    }
  }, [initialFiltersLoaded, permissionsLoading, hasSingleStatus, singleStatus, statusFilterApplied]);

  const handleChangePage = (event, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const toggleSelectedContractId = (id) => {
    setSelectedContractIds((prev) => {
      const set = new Set(prev);
      if (set.has(id)) set.delete(id);
      else set.add(id);
      return Array.from(set);
    });
  };

  const toggleSelectAllOnPage = (checked) => {
    setSelectedContractIds((prev) => {
      const set = new Set(prev);
      if (checked) contracts.forEach((c) => set.add(c.id));
      else contracts.forEach((c) => set.delete(c.id));
      return Array.from(set);
    });
  };

  const handleOpenBulkEdit = () => {
    if (!selectedContractIds.length) {
      alert("Selecione pelo menos 1 contrato.");
      return;
    }
    setBulkTabIndex(0);
    setBulkContract({
      number: "",
      date: "",
      category: "",
      status: "",
      customer: null,
      bank: null,
      lawyer: null,
      lawyer_wallet: null,
      service_obs: "",
      process_protocol_obs: "",
      filed_at: "",
      file_at: "",
      state: "",
      process_number: "",
      complaint_protocol_number: "",
      sales_consultant: null,
      contracted_value: null,
      installments: null,
      installment_value: null,
      contract_expenses_value: null,
      installment_part_not_questioned_value: null,
      installment_questioned_value: null,
      interest_rate_monthly_value: null,
      interest_rate_yearly_value: null,
      contract_at: "",
      first_installment_at: "",
      grace_period_days: null,
      installments_payed: null,
      is_renegotiation: null,
      renegotiation_installments: null,
      renegotiation_value: null,
      contract_type: null,
      interest_rate_average_monthly_value: null,
      interest_rate_average_yearly_value: null,
      updated_at: "",
      contract_installments: [],
      total_amortization: null,
      total_installment_value: null,
      total_overpaid_value: null,
      total_legal_interest: null,
      total_correction_value: null,
      responsible_user: null,
      has_ajg: null,
      has_after_sales_video: null,
      declares_irpf: null,
    });
    setOpenBulkEditModal(true);
  };

  const handleCloseBulkEdit = () => {
    setOpenBulkEditModal(false);
  };

  const handleSubmitBulkEdit = async () => {
    if (!selectedContractIds.length) {
      alert("Selecione pelo menos 1 contrato.");
      return;
    }

    const statusesRequiringMotivo = new Set(["CANCELADO", "FALTAM_DOCUMENTOS"]);
    if (bulkContract.status && statusesRequiringMotivo.has(bulkContract.status)) {
      if (!String(bulkContract.process_protocol_obs || "").trim()) {
        alert("Para o status selecionado, informe a observação do protocolo do processo.");
        return;
      }
    }

    const payload = { contract_ids: selectedContractIds };

    // Campos editáveis em massa (quando preenchidos)
    if (bulkContract.customer?.id) payload.customer_id = bulkContract.customer.id;
    if (bulkContract.bank?.id) payload.bank_id = bulkContract.bank.id;
    if (bulkContract.date) payload.date = bulkContract.date;
    if (bulkContract.category) payload.category = bulkContract.category;
    if (bulkContract.process_number && String(bulkContract.process_number).trim()) payload.process_number = bulkContract.process_number;
    if (bulkContract.complaint_protocol_number && String(bulkContract.complaint_protocol_number).trim()) payload.complaint_protocol_number = bulkContract.complaint_protocol_number;
    if (bulkContract.status) payload.status = bulkContract.status;
    if (bulkContract.filed_at) payload.filed_at = bulkContract.filed_at;
    if (bulkContract.file_at) payload.file_at = bulkContract.file_at;
    if (bulkContract.state && String(bulkContract.state).trim()) payload.state = bulkContract.state;
    if (bulkContract.service_obs && String(bulkContract.service_obs).trim()) payload.service_obs = bulkContract.service_obs;
    if (bulkContract.process_protocol_obs && String(bulkContract.process_protocol_obs).trim()) payload.process_protocol_obs = bulkContract.process_protocol_obs;

    // responsável: envia apenas se selecionado
    if (bulkContract.responsible_user?.id) payload.responsible_user_id = bulkContract.responsible_user.id;
    if (bulkContract.lawyer?.id) payload.lawyer_id = bulkContract.lawyer.id;
    if (bulkContract.sales_consultant?.id) payload.sales_consultant_id = bulkContract.sales_consultant.id;
    if (bulkContract.lawyer_wallet?.id) payload.lawyer_wallet_id = bulkContract.lawyer_wallet.id;

    // tri-state boolean: null = não alterar
    if (bulkContract.has_ajg !== null) payload.has_ajg = !!bulkContract.has_ajg;
    if (bulkContract.has_after_sales_video !== null) payload.has_after_sales_video = !!bulkContract.has_after_sales_video;
    if (bulkContract.declares_irpf !== null) payload.declares_irpf = !!bulkContract.declares_irpf;

    if (Object.keys(payload).length <= 1) {
      alert("Selecione ao menos um campo para alterar.");
      return;
    }

    setBulkEditSubmitting(true);
    try {
      const res = await fetch("/backend/contracts/bulk-update/", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        credentials: "include",
      });
      const result = await res.json().catch(() => ({}));
      if (!res.ok && res.status !== 207) {
        throw new Error(result?.error || result?.detail || `Falha ao atualizar (status ${res.status})`);
      }

      const updated = result?.updated ?? 0;
      const errors = Array.isArray(result?.errors) ? result.errors : [];
      if (errors.length) {
        alert(
          `Atualização em massa concluída com avisos.\n\nAtualizados: ${updated}\nErros: ${errors.length}\n\n` +
            errors
              .slice(0, 10)
              .map((e) => `ID ${e.id}: ${typeof e.errors === "string" ? e.errors : JSON.stringify(e.errors)}`)
              .join("\n")
        );
      } else {
        alert(`Atualização em massa concluída.\n\nAtualizados: ${updated}`);
      }

      setOpenBulkEditModal(false);
      setSelectedContractIds([]);
      setSelectionEnabled(false);
      fetchContracts();
    } catch (e) {
      alert(`Erro na atualização em massa:\n\n${e.message || e}`);
    } finally {
      setBulkEditSubmitting(false);
    }
  };

  const handleBulkDelete = async () => {
    if (!selectedContractIds.length) {
      alert("Selecione pelo menos 1 contrato.");
      return;
    }

    const count = selectedContractIds.length;
    const confirmed = window.confirm(
      `Deseja excluir ${count} contrato(s) selecionado(s)?\n\nOs registros serão removidos da listagem, mas poderão ser restaurados depois.`
    );
    if (!confirmed) return;

    setBulkDeleting(true);
    try {
      const res = await fetch("/backend/contracts/bulk-delete/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ contract_ids: selectedContractIds }),
        credentials: "include",
      });
      const result = await res.json().catch(() => ({}));

      if (!res.ok && res.status !== 207) {
        throw new Error(result?.error || result?.detail || `Falha ao excluir (status ${res.status})`);
      }

      const deleted = result?.deleted ?? 0;
      const errors = Array.isArray(result?.errors) ? result.errors : [];
      if (errors.length) {
        alert(
          `Exclusão em massa concluída com avisos.\n\nExcluídos: ${deleted}\nErros: ${errors.length}\n\n` +
            errors
              .slice(0, 10)
              .map((e) => `ID ${e.id}: ${typeof e.errors === "string" ? e.errors : JSON.stringify(e.errors)}`)
              .join("\n")
        );
      } else {
        alert(`Exclusão em massa concluída.\n\nExcluídos: ${deleted} contrato(s).`);
      }

      setSelectedContractIds([]);
      setSelectionEnabled(false);
      fetchContracts();
    } catch (e) {
      alert(`Erro na exclusão em massa:\n\n${e.message || e}`);
    } finally {
      setBulkDeleting(false);
    }
  };

  const handleBulkRestore = async () => {
    if (!selectedContractIds.length) {
      alert("Selecione pelo menos 1 contrato.");
      return;
    }

    const count = selectedContractIds.length;
    const confirmed = window.confirm(
      `Deseja restaurar ${count} contrato(s) selecionado(s)?`
    );
    if (!confirmed) return;

    setBulkRestoring(true);
    try {
      const res = await fetch("/backend/contracts/bulk-restore/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ contract_ids: selectedContractIds }),
        credentials: "include",
      });
      const result = await res.json().catch(() => ({}));

      if (!res.ok && res.status !== 207) {
        throw new Error(result?.error || result?.detail || `Falha ao restaurar (status ${res.status})`);
      }

      const restored = result?.restored ?? 0;
      const errors = Array.isArray(result?.errors) ? result.errors : [];
      if (errors.length) {
        alert(
          `Restauração em massa concluída com avisos.\n\nRestaurados: ${restored}\nErros: ${errors.length}\n\n` +
            errors
              .slice(0, 10)
              .map((e) => `ID ${e.id}: ${typeof e.errors === "string" ? e.errors : JSON.stringify(e.errors)}`)
              .join("\n")
        );
      } else {
        alert(`Restauração em massa concluída.\n\nRestaurados: ${restored} contrato(s).`);
      }

      setSelectedContractIds([]);
      setSelectionEnabled(false);
      fetchContracts();
    } catch (e) {
      alert(`Erro na restauração em massa:\n\n${e.message || e}`);
    } finally {
      setBulkRestoring(false);
    }
  };

  refreshListRef.current = fetchContracts;

  const notifyContractsSaved = () => refreshListRef.current?.();

  const handleAdd = () => {
    openNewContract({ onSaved: notifyContractsSaved });
  };

  const handleEdit = (contract) => {
    openContract(contract, { onSaved: notifyContractsSaved });
  };

  const handleViewHistory = (contract) => {
    setSelectedContractForHistory(contract);
    setOpenHistoryModal(true);
  };

  const handleCloseHistoryModal = () => {
    setOpenHistoryModal(false);
    setSelectedContractForHistory(null);
  };

  const handleFile = (contract) => {
    setSelectedContractForFile(contract);
    setOpenFileModal(true);
  };

  const handleCloseFileModal = () => {
    setOpenFileModal(false);
    setSelectedContractForFile(null);
  };

  const handleDownloadPack = (contract) => {
    setSelectedContractForDownloadPack(contract);
    setOpenDownloadPackModal(true);
  };

  const handleCloseDownloadPackModal = () => {
    setOpenDownloadPackModal(false);
    setSelectedContractForDownloadPack(null);
  };

  const handleFileSave = (updatedContract) => {
    setContracts((prevContracts) =>
      prevContracts.map((contract) =>
        contract.id === updatedContract.id ? updatedContract : contract
      )
    );
    fetchContracts();
  };

  const handleDelete = async (contractId) => {
    if (window.confirm("Você tem certeza que deseja excluir este contrato?")) {
      try {
        const response = await fetch(`/backend/contracts/${contractId}/`, {
          method: "DELETE",
        });

        if (response.ok) {
          alert("Contrato excluído com sucesso!");
          try {
            await fetchContracts();
          } catch (fetchError) {
            console.error("Erro ao recarregar lista após excluir:", fetchError);
          }
          return;
        }

        // Processa erros do backend
        let errorMessage = "";
        
        try {
          const errorData = await response.json();
          
          // Processa diferentes tipos de erro do backend
          if (errorData.detail) {
            errorMessage = errorData.detail;
          } else if (errorData.error) {
            errorMessage = errorData.error;
          } else if (errorData.message) {
            errorMessage = errorData.message;
          } else if (typeof errorData === 'string') {
            errorMessage = errorData;
          } else {
            // Tenta extrair mensagens de campos específicos
            const errorMessages = [];
            Object.keys(errorData).forEach((key) => {
              const fieldErrors = Array.isArray(errorData[key])
                ? errorData[key]
                : [errorData[key]];
              fieldErrors.forEach((error) => {
                errorMessages.push(`${key}: ${error}`);
              });
            });
            errorMessage = errorMessages.length > 0
              ? errorMessages.join("\n")
              : "Não foi possível excluir o contrato.";
          }
        } catch (parseError) {
          // Se não conseguir parsear o JSON, usa mensagem baseada no status
          if (response.status === 404) {
            errorMessage = `Contrato não encontrado. O contrato com ID ${contractId} não existe ou já foi removido.`;
          } else if (response.status === 400) {
            errorMessage = "Não é possível excluir este contrato. Ele pode estar vinculado a outros registros.";
          } else if (response.status === 403) {
            errorMessage = "Você não tem permissão para excluir este contrato.";
          } else if (response.status === 500) {
            errorMessage = "Erro interno do servidor ao excluir o contrato. Tente novamente mais tarde ou entre em contato com o suporte.";
          } else {
            errorMessage = `Erro ao excluir contrato (Status: ${response.status}). Tente novamente.`;
          }
        }
        
        alert(`Erro ao excluir contrato:\n\n${errorMessage}`);
      } catch (error) {
        console.error("Erro ao excluir contrato:", error);
        alert(`Erro ao excluir contrato:\n\n${error.message || "Erro de conexão. Verifique sua internet e tente novamente."}`);
      }
    }
  };

  const handleRestore = async (contractId) => {
    if (window.confirm("Deseja restaurar este contrato?")) {
      try {
        const response = await fetch(`/backend/contracts/${contractId}/restore/`, {
          method: "POST",
        });
        if (response.ok) {
          alert("Contrato restaurado com sucesso!");
          await fetchContracts();
        } else {
          let errorMessage = "Não foi possível restaurar o contrato.";
          try {
            const errorData = await response.json();
            errorMessage = errorData.error || errorData.detail || errorMessage;
          } catch (_) {}
          alert(`Erro ao restaurar contrato:\n\n${errorMessage}`);
        }
      } catch (error) {
        console.error("Erro ao restaurar contrato:", error);
        alert(`Erro ao restaurar contrato:\n\n${error.message || "Erro de conexão."}`);
      }
    }
  };

  const handlePrint = async (contractId) => {
    const listedContract = contracts.find((contract) => contract.id === contractId);
    if (!confirmGenerateDespiteNonAbusiveRate(listedContract || getCurrentContract())) {
      return;
    }

    try {
      const response = await fetch(`/backend/contracts/${contractId}/generate-docx/`, {
        method: "GET",
      });

      if (!response.ok) {
        let errorMessage = "";
        const missingFields = [];
        
        try {
          const errorData = await response.json();
          
          // Verifica se há campos obrigatórios faltantes
          if (errorData.missing_fields && Array.isArray(errorData.missing_fields)) {
            missingFields.push(...errorData.missing_fields);
          }
          
          // Processa diferentes tipos de erro do backend
          if (missingFields.length > 0) {
            // Monta mensagem destacando os campos obrigatórios faltantes
            const messageParts = [];
            messageParts.push("⚠️ CAMPOS OBRIGATÓRIOS NÃO PREENCHIDOS:");
            messageParts.push("");
            messageParts.push("Os seguintes campos precisam ser preenchidos antes de gerar o documento:");
            messageParts.push("");
            missingFields.forEach((field, idx) => {
              messageParts.push(`${idx + 1}. ${field}`);
            });
            
            if (errorData.details) {
              messageParts.push("");
              messageParts.push(`Detalhes: ${errorData.details}`);
            }
            
            errorMessage = messageParts.join("\n");
          } else if (errorData.detail) {
            errorMessage = errorData.detail;
          } else if (errorData.error) {
            errorMessage = errorData.error;
            // Se há details, adiciona
            if (errorData.details) {
              errorMessage += `\n\n${errorData.details}`;
            }
          } else if (errorData.message) {
            errorMessage = errorData.message;
          } else if (errorData.details) {
            errorMessage = errorData.details;
          } else if (typeof errorData === 'string') {
            errorMessage = errorData;
          } else {
            // Tenta extrair mensagens de campos específicos
            const errorMessages = [];
            Object.keys(errorData).forEach((key) => {
              if (key !== 'missing_fields' && key !== 'details') {
                const fieldErrors = Array.isArray(errorData[key])
                  ? errorData[key]
                  : [errorData[key]];
                fieldErrors.forEach((error) => {
                  errorMessages.push(`${key}: ${error}`);
                });
              }
            });
            errorMessage = errorMessages.length > 0
              ? errorMessages.join("\n")
              : "Não foi possível gerar o documento do contrato.";
          }
        } catch (parseError) {
          // Se não conseguir parsear o JSON, usa mensagem baseada no status
          if (response.status === 404) {
            errorMessage = `Contrato não encontrado. O contrato com ID ${contractId} não existe ou foi removido.`;
          } else if (response.status === 400) {
            errorMessage = "Dados inválidos para gerar o documento. Verifique se o contrato está completo.";
          } else if (response.status === 500) {
            errorMessage = "Erro interno do servidor ao gerar o documento. Tente novamente mais tarde ou entre em contato com o suporte.";
          } else if (response.status === 403) {
            errorMessage = "Você não tem permissão para gerar o documento deste contrato.";
          } else {
            errorMessage = `Erro ao gerar documento do contrato (Status: ${response.status}). Tente novamente.`;
          }
        }
        
        throw new Error(errorMessage);
      }

      // Verifica se a resposta é realmente um arquivo DOCX
      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/vnd.openxmlformats-officedocument.wordprocessingml.document")) {
        const text = await response.text();
        try {
          const errorData = JSON.parse(text);
          throw new Error(errorData.detail || errorData.error || errorData.message || "O servidor retornou uma resposta inválida.");
        } catch {
          throw new Error("O servidor retornou uma resposta inesperada ao tentar gerar o documento.");
        }
      }

      // Cria um blob com o DOCX retornado
      const blob = await response.blob();
      
      // Verifica se o blob não está vazio
      if (blob.size === 0) {
        throw new Error("O documento gerado está vazio. Verifique se o contrato possui todos os dados necessários.");
      }
      
      // Cria uma URL temporária para o blob
      const url = window.URL.createObjectURL(blob);
      
      // Cria um link temporário e clica nele para iniciar o download
      const link = document.createElement("a");
      link.href = url;
      link.download = `contrato_${contractId}.docx`;
      document.body.appendChild(link);
      link.click();
      
      // Remove o link e libera a URL
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error("Erro ao imprimir contrato:", error);
      alert(`Erro ao gerar documento do contrato:\n\n${error.message || "Erro desconhecido. Tente novamente ou entre em contato com o suporte."}`);
    }
  };

  const handlePrintInitialCalculation = async (contractId) => {
    const listedContract = contracts.find((contract) => contract.id === contractId);
    if (!confirmGenerateDespiteNonAbusiveRate(listedContract || getCurrentContract())) {
      return;
    }

    try {
      const response = await fetch(`/backend/contracts/${contractId}/generate-initial-calculation-docx/`, {
        method: "GET",
      });

      if (!response.ok) {
        let errorMessage = "";
        const missingFields = [];
        
        try {
          const errorData = await response.json();
          
          // Verifica se há campos obrigatórios faltantes
          if (errorData.missing_fields && Array.isArray(errorData.missing_fields)) {
            missingFields.push(...errorData.missing_fields);
          }
          
          // Processa diferentes tipos de erro do backend
          if (missingFields.length > 0) {
            // Monta mensagem destacando os campos obrigatórios faltantes
            const messageParts = [];
            messageParts.push("⚠️ CAMPOS OBRIGATÓRIOS NÃO PREENCHIDOS:");
            messageParts.push("");
            messageParts.push("Os seguintes campos precisam ser preenchidos antes de gerar o cálculo inicial:");
            messageParts.push("");
            missingFields.forEach((field, idx) => {
              messageParts.push(`${idx + 1}. ${field}`);
            });
            
            if (errorData.details) {
              messageParts.push("");
              messageParts.push(`Detalhes: ${errorData.details}`);
            }
            
            errorMessage = messageParts.join("\n");
          } else if (errorData.detail) {
            errorMessage = errorData.detail;
          } else if (errorData.error) {
            errorMessage = errorData.error;
            // Se há details, adiciona
            if (errorData.details) {
              errorMessage += `\n\n${errorData.details}`;
            }
          } else if (errorData.message) {
            errorMessage = errorData.message;
          } else if (errorData.details) {
            errorMessage = errorData.details;
          } else if (typeof errorData === 'string') {
            errorMessage = errorData;
          } else {
            // Tenta extrair mensagens de campos específicos
            const errorMessages = [];
            Object.keys(errorData).forEach((key) => {
              if (key !== 'missing_fields' && key !== 'details') {
                const fieldErrors = Array.isArray(errorData[key])
                  ? errorData[key]
                  : [errorData[key]];
                fieldErrors.forEach((error) => {
                  errorMessages.push(`${key}: ${error}`);
                });
              }
            });
            errorMessage = errorMessages.length > 0
              ? errorMessages.join("\n")
              : "Não foi possível gerar o cálculo inicial do contrato.";
          }
        } catch (parseError) {
          // Se não conseguir parsear o JSON, usa mensagem baseada no status
          if (response.status === 404) {
            errorMessage = `Contrato não encontrado. O contrato com ID ${contractId} não existe ou foi removido.`;
          } else if (response.status === 400) {
            errorMessage = "Dados inválidos para gerar o cálculo inicial. Verifique se o contrato possui todas as parcelas calculadas.";
          } else if (response.status === 500) {
            errorMessage = "Erro interno do servidor ao gerar o cálculo inicial. Tente novamente mais tarde ou entre em contato com o suporte.";
          } else if (response.status === 403) {
            errorMessage = "Você não tem permissão para gerar o cálculo inicial deste contrato.";
          } else {
            errorMessage = `Erro ao gerar cálculo inicial do contrato (Status: ${response.status}). Tente novamente.`;
          }
        }
        
        throw new Error(errorMessage);
      }

      // Obtém o HTML retornado
      const htmlContent = await response.text();
      
      // Verifica se o conteúdo não está vazio
      if (!htmlContent || htmlContent.trim().length === 0) {
        throw new Error("O cálculo inicial gerado está vazio. Verifique se o contrato possui todas as parcelas calculadas.");
      }
      
      // Abre uma nova aba com o HTML do cálculo inicial
      const newWindow = window.open('', '_blank');
      if (newWindow) {
        newWindow.document.write(htmlContent);
        newWindow.document.close();
        newWindow.focus(); // Foca na nova aba
      } else {
        alert("Pop-ups bloqueados. Por favor, permita pop-ups para este site e tente novamente.");
      }
      
    } catch (error) {
      console.error("Erro ao imprimir cálculo inicial:", error);
      alert(`Erro ao gerar cálculo inicial do contrato:\n\n${error.message || "Erro desconhecido. Tente novamente ou entre em contato com o suporte."}`);
    }
  };

  const handleExportCSV = async () => {
    setIsExporting(true);
    try {
      const params = new URLSearchParams();
      
      // Adiciona busca geral se houver
      if (searchTerm !== "") {
        params.append("contains", searchTerm);
      }

      // Filtro por status de importação
      if (importedStatusFilter !== "all") {
        params.append("imported_status", importedStatusFilter);
      }

      // Filtro por contratos com dados faltantes
      if (missingDataFilter === "yes") {
        params.append("missing_data", "1");
      }
      
      // Adiciona filtros avançados
      Object.keys(filters).forEach((key) => {
        const value = filters[key];
        if (value !== null && value !== "" && value !== undefined) {
          params.append(key, value);
        }
      });

      const response = await fetch(`/backend/contracts/export-csv/?${params.toString()}`);
      
      if (!response.ok) {
        let errorMessage = "";
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorData.detail || "Erro ao exportar contratos";
        } catch {
          errorMessage = `Erro ao exportar contratos (Status: ${response.status})`;
        }
        throw new Error(errorMessage);
      }

      // Cria um blob com o CSV retornado
      const blob = await response.blob();
      
      // Verifica se o blob não está vazio
      if (blob.size === 0) {
        throw new Error("O arquivo CSV gerado está vazio.");
      }
      
      // Cria uma URL temporária para o blob
      const url = window.URL.createObjectURL(blob);
      
      // Cria um link temporário e clica nele para iniciar o download
      const link = document.createElement("a");
      link.href = url;
      
      // Extrai o nome do arquivo do header Content-Disposition se disponível
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'contratos.csv';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      
      // Remove o link e libera a URL
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error("Erro ao exportar CSV:", error);
      alert(`Erro ao exportar contratos:\n\n${error.message || "Erro desconhecido. Tente novamente."}`);
    } finally {
      setIsExporting(false);
    }
  };

  const handleSyncGoogleSheets = async () => {
    if (
      !window.confirm(
        "Reescrever a planilha Google Sheets com todos os contratos ativos?\n\nIsso pode levar alguns segundos."
      )
    ) {
      return;
    }
    setIsSyncingSheets(true);
    try {
      const response = await fetch("/backend/contracts/sheets-sync/", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(
          result?.error || result?.detail || `Falha ao sincronizar (status ${response.status})`
        );
      }
      const count = result?.count ?? 0;
      const link = result?.webViewLink;
      const openLink = link
        ? window.confirm(
            `Planilha atualizada com ${count} contrato(s).\n\nDeseja abrir a planilha no Google Sheets?`
          )
        : false;
      if (openLink && link) {
        window.open(link, "_blank", "noopener,noreferrer");
      } else if (!link) {
        alert(`Planilha atualizada com ${count} contrato(s).`);
      }
    } catch (error) {
      console.error("Erro ao sincronizar Google Sheets:", error);
      alert(
        `Erro ao sincronizar com Google Sheets:\n\n${error.message || "Erro desconhecido. Verifique a conexão do Drive e tente novamente."}`
      );
    } finally {
      setIsSyncingSheets(false);
    }
  };

  const handleDownloadTemplate = () => {
    window.open("/backend/contracts/csv-template/", "_blank");
  };

  const handleOpenImportModal = (mode = "processos") => {
    setImportMode(mode);
    setOpenImportModal(true);
    setSelectedFile(null);
    setImportResult(null);
    setDeleteImportedBefore(false);
  };

  const handleCloseImportModal = () => {
    setOpenImportModal(false);
    setSelectedFile(null);
    setImportResult(null);
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.name.endsWith(".csv")) {
        setSelectedFile(file);
        setImportResult(null);
      } else {
        alert("Por favor, selecione um arquivo CSV válido. O arquivo deve ter a extensão .csv");
        event.target.value = "";
      }
    }
  };

  const handleImportCSV = async () => {
    if (!selectedFile) {
      alert("Por favor, selecione um arquivo CSV antes de importar.");
      return;
    }

    setImporting(true);
    setImportResult(null);

    const formData = new FormData();
    formData.append("csv_file", selectedFile);
    if (importMode === "processos") {
      formData.append("delete_imported", deleteImportedBefore ? "true" : "false");
    }

    const importUrl = importMode === "export"
      ? "/backend/contracts/import-export-csv/"
      : "/backend/contracts/import-csv/";

    try {
      const response = await fetch(importUrl, {
        method: "POST",
        body: formData,
        credentials: "include",
      });

      const contentType = response.headers.get("content-type") || "";
      let result;
      try {
        result = contentType.includes("application/json")
          ? await response.json()
          : { error: `Servidor retornou ${response.status}. Verifique se está logado.` };
      } catch {
        result = {
          error:
            response.status === 401 || response.status === 403
              ? "Sessão expirada. Faça login novamente."
              : response.status === 504
                ? "Tempo limite excedido. Parte dos registros pode ter sido importada. Verifique a lista e tente importar em lotes menores se necessário."
                : "Resposta inválida do servidor. Tente novamente.",
        };
      }

      if (response.ok || response.status === 207) {
        setImportResult(result);
        fetchContracts();
      } else {
        throw new Error(
          result.error ||
            "Não foi possível importar o arquivo CSV. Verifique o formato do arquivo e tente novamente."
        );
      }
    } catch (error) {
      console.error("Erro ao importar CSV:", error);
      setImportResult({
        success: false,
        error: error.message,
      });
    } finally {
      setImporting(false);
    }
  };

  const labelDisplayedRows = ({ from, to, count }) =>
    `${from}–${to} de ${count} (${totalCustomers} CPFs)`;

  return (
    <div className="w-full overflow-auto">
      <MainCard
        title="Cadastro de contratos"
        actions={[
          <Tooltip key="template-csv" title="Baixar template para importação de contratos">
            <Button
              variant="outlined"
              color="secondary"
              onClick={handleDownloadTemplate}
              startIcon={<IconDownload stroke={1.5} size="1.1rem" />}
              sx={{ mr: 1 }}
            >
              Template CSV
            </Button>
          </Tooltip>,
          ...(canImportData ? [
          <Tooltip key="import-csv" title="Importar contratos via CSV (processos)">
            <Button
              variant="outlined"
              onClick={() => handleOpenImportModal("processos")}
              startIcon={<IconUpload stroke={1.5} size="1.1rem" />}
              sx={{ mr: 1 }}
            >
              Importar CSV
            </Button>
          </Tooltip>,
          <Tooltip key="import-export-csv" title="Importar CSV exportado pelo sistema (42 colunas)">
            <Button
              variant="outlined"
              color="info"
              onClick={() => handleOpenImportModal("export")}
              startIcon={<IconFileSpreadsheet stroke={1.5} size="1.1rem" />}
              sx={{ mr: 1 }}
            >
              Importar exportação
            </Button>
          </Tooltip>,
          ] : []),
          <Button
            key="export-csv"
            variant="contained"
            color="success"
            onClick={handleExportCSV}
            disabled={isExporting}
            startIcon={<IconFileSpreadsheet stroke={1.5} size="1.1rem" />}
            sx={{ mr: 1 }}
          >
            {isExporting ? "Exportando..." : "Exportar CSV"}
          </Button>,
          ...(isAdmin
            ? [
                <Tooltip
                  key="sync-sheets"
                  title="Reescreve a planilha Google Sheets (pasta Contratos) com todos os contratos ativos"
                >
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={handleSyncGoogleSheets}
                    disabled={isSyncingSheets}
                    startIcon={<IconCloudUpload stroke={1.5} size="1.1rem" />}
                    sx={{ mr: 1 }}
                  >
                    {isSyncingSheets ? "Sincronizando..." : "Atualizar Sheets"}
                  </Button>
                </Tooltip>,
              ]
            : []),
          ...(permissions.can_create_contracts ? [
            <AddButton key="add-contract" onClick={handleAdd}>
              <IconPlus stroke={1.5} size="1.1rem" sx={{ mr: 5 }} />
              Adicionar contrato
            </AddButton>,
          ] : []),
        ]}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap", mb: 2 }}>
          <SearchSection
            value={searchTerm}
            onSearchChange={(value) => { setSearchTerm(value); setPage(0); }}
          />
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel id="contracts-imported-status-filter-label">Status importado</InputLabel>
            <Select
              labelId="contracts-imported-status-filter-label"
              id="contracts-imported-status-filter"
              value={importedStatusFilter}
              label="Status importado"
              onChange={(e) => {
                setImportedStatusFilter(e.target.value);
                setPage(0);
              }}
            >
              <MenuItem value="all">Todos</MenuItem>
              <MenuItem value="imported">Somente importados</MenuItem>
              <MenuItem value="not_imported">Somente não importados</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel id="contracts-missing-data-filter-label">Dados obrigatórios</InputLabel>
            <Select
              labelId="contracts-missing-data-filter-label"
              id="contracts-missing-data-filter"
              value={missingDataFilter}
              label="Dados obrigatórios"
              onChange={(e) => {
                setMissingDataFilter(e.target.value);
                setPage(0);
              }}
            >
              <MenuItem value="all">Todos</MenuItem>
              <MenuItem value="yes">Com dados faltantes</MenuItem>
            </Select>
          </FormControl>
          {canRestore && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={showDeletedContracts}
                  onChange={(e) => {
                    setShowDeletedContracts(e.target.checked);
                    setPage(0);
                  }}
                  color="primary"
                />
              }
              label="Contratos excluídos"
              sx={{ ml: 1 }}
            />
          )}
        </Box>
        <Divider sx={{ my: 2 }} />
        <ContractsFilters
          customers={customers}
          banks={banks}
          contractTypes={contractTypes}
          lawyers={lawyers}
          salesConsultants={salesConsultants}
          users={users}
          contractCategories={contractCategories}
          contractStatuses={contractStatuses}
          filters={filters}
          searchTerm={searchTerm}
          importedStatusFilter={importedStatusFilter}
          missingDataFilter={missingDataFilter}
          showDeletedContracts={showDeletedContracts}
          onApplySavedFilter={applyFiltersSnapshot}
          onDefaultFilterLoaded={handleDefaultFilterLoaded}
          onInitialFiltersReady={handleInitialFiltersReady}
          onFiltersChange={(newFilters) => {
            setFilters(newFilters);
            setPage(0); // Reset para primeira página ao aplicar filtros
          }}
            onClearFilters={() => {
            setFilters({ ...EMPTY_CONTRACT_FILTERS });
            setPage(0);
          }}
        />
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap", mt: 1, mb: 1 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={selectionEnabled}
                onChange={(e) => setSelectionEnabled(e.target.checked)}
              />
            }
            label="Selecionar vários"
          />
          {selectionEnabled && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
              <Typography variant="body2">
                Selecionados: <strong>{selectedContractIds.length}</strong>
              </Typography>
              <Button
                size="small"
                variant="contained"
                onClick={handleOpenBulkEdit}
                disabled={selectedContractIds.length === 0 || bulkDeleting || bulkRestoring || showDeletedContracts}
              >
                Editar selecionados
              </Button>
              {permissions.can_delete_contracts && !showDeletedContracts && (
                <Button
                  size="small"
                  variant="contained"
                  color="error"
                  onClick={handleBulkDelete}
                  disabled={selectedContractIds.length === 0 || bulkDeleting || bulkEditSubmitting || bulkRestoring}
                >
                  {bulkDeleting ? "Excluindo..." : "Excluir selecionados"}
                </Button>
              )}
              {permissions.can_restore_contracts && showDeletedContracts && (
                <Button
                  size="small"
                  variant="contained"
                  color="success"
                  onClick={handleBulkRestore}
                  disabled={selectedContractIds.length === 0 || bulkRestoring || bulkDeleting || bulkEditSubmitting}
                >
                  {bulkRestoring ? "Restaurando..." : "Restaurar selecionados"}
                </Button>
              )}
              <Button
                size="small"
                variant="outlined"
                onClick={() => setSelectedContractIds([])}
                disabled={selectedContractIds.length === 0 || bulkDeleting || bulkRestoring}
              >
                Limpar seleção
              </Button>
            </Box>
          )}
          {contracts.length > 0 && (
            <Box sx={{ marginLeft: "auto" }}>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50, 100, 150]}
                component="div"
                count={totalRecords}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Contratos por página"
                labelDisplayedRows={labelDisplayedRows}
              />
            </Box>
          )}
        </Box>
        <Divider sx={{ my: 2 }} />
        <Box
          ref={tableScrollRef}
          onMouseDown={handleTableScrollMouseDown}
          sx={{
            overflowX: "auto",
            cursor: isTableDragging
              ? "grabbing"
              : isTableSelectionMode
                ? "text"
                : "grab",
            userSelect: isTableSelectionMode ? "text" : "none",
            WebkitUserSelect: isTableSelectionMode ? "text" : "none",
            "& button:not(.customer-name-button), & input, & a, & .MuiCheckbox-root": {
              cursor: "pointer",
              userSelect: "none",
              WebkitUserSelect: "none",
            },
            "& .customer-name-button": {
              cursor: isTableSelectionMode ? "text" : "pointer",
              userSelect: isTableSelectionMode ? "text" : "none",
              WebkitUserSelect: isTableSelectionMode ? "text" : "none",
            },
          }}
        >
          <ContractsTable
            contracts={contracts}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onRestore={handleRestore}
            onPrint={handlePrint}
            onPrintInitialCalculation={handlePrintInitialCalculation}
            onViewHistory={handleViewHistory}
            onFile={handleFile}
            onDownloadPack={handleDownloadPack}
            formatDocument={formatDocument}
            permissions={permissions}
            selectionEnabled={selectionEnabled}
            selectedIds={selectedContractIds}
            onToggleSelect={toggleSelectedContractId}
            onToggleSelectAll={toggleSelectAllOnPage}
            showDeletedMode={showDeletedContracts}
          />
        </Box>
      </MainCard>

      {contracts.length > 0 && (
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50, 100, 150]}
          component="div"
          count={totalRecords}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Contratos por página"
          labelDisplayedRows={labelDisplayedRows}
        />
      )}

      {/* Modal de Histórico de Status */}
      <ContractStatusHistoryModal
        open={openHistoryModal}
        onClose={handleCloseHistoryModal}
        contract={selectedContractForHistory}
      />

      {/* Modal de Ajuizar */}
      <ContractFileModal
        open={openFileModal}
        onClose={handleCloseFileModal}
        contract={selectedContractForFile}
        onSave={handleFileSave}
      />

      {/* Modal de Download do Pack */}
      <ContractDownloadPackModal
        open={openDownloadPackModal}
        onClose={handleCloseDownloadPackModal}
        contract={selectedContractForDownloadPack}
      />

      {/* Modal de Edição em Massa (placeholder - será implementado a seguir) */}
      <Dialog open={openBulkEditModal} onClose={handleCloseBulkEdit} maxWidth="sm" fullWidth>
        <DialogTitle>Editar contratos selecionados</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mt: 2 }}>
            As alterações abaixo serão aplicadas a <strong>{selectedContractIds.length}</strong> contrato(s). Campos vazios / “Não alterar” serão ignorados.
          </Alert>
          <Box sx={{ mt: 2 }}>
            <ContractForm
              currentContract={bulkContract}
              setCurrentContract={setBulkContract}
              customers={customers}
              banks={banks}
              lawyers={lawyers}
              salesConsultants={salesConsultants}
              contractTypes={contractTypes}
              contractCategories={contractCategories}
              contractStatuses={allContractStatuses}
              users={users}
              tabIndex={bulkTabIndex}
              setTabIndex={setBulkTabIndex}
              formatDocument={formatDocument}
              calculatePgto={calculatePgto}
              calculateInstallmentQuestionedValue={calculateInstallmentQuestionedValue}
              calculateYearlyInterest={calculateYearlyInterest}
              calculateDaysDifference={calculateDaysDifference}
              setInterestRateAverageMonthlyValue={() => {}}
              onCustomerChange={null}
              customerBankIds={[]}
              permissions={permissions}
              isEditing={true}
              fieldsWithErrors={[]}
              bulkMode={true}
              bulkEditableFields={[
                "customer",
                "bank",
                "date",
                "category",
                "process_number",
                "complaint_protocol_number",
                "status",
                "lawyer",
                "responsible_user",
                "filed_at",
                "file_at",
                "state",
                "sales_consultant",
                "lawyer_wallet",
                "service_obs",
                "process_protocol_obs",
                "has_ajg",
                "has_after_sales_video",
                "declares_irpf",
              ]}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseBulkEdit} color="secondary" disabled={bulkEditSubmitting}>Cancelar</Button>
          <Button onClick={handleSubmitBulkEdit} color="primary" disabled={bulkEditSubmitting}>
            {bulkEditSubmitting ? "Salvando..." : "Aplicar alterações"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Importação CSV */}
      <Dialog open={openImportModal} onClose={handleCloseImportModal} maxWidth="md" fullWidth>
        <DialogTitle>
          {importMode === "export" ? "Importar CSV de exportação" : "Importar Contratos via CSV"}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {importMode === "export" ? (
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Faça upload do arquivo gerado pelo botão <strong>Exportar CSV</strong> (42 colunas, delimitador <strong>;</strong>).
                Colunas obrigatórias: <strong>CPF Cliente</strong> e <strong>Banco</strong>.
                A coluna <strong>Data de Ajuizamento</strong> atualiza a data em que o processo foi ajuizado.
                Contratos com <strong>ID</strong> existente serão atualizados; demais serão criados.
                O cliente deve estar cadastrado antes.
              </Typography>
            ) : (
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Faça upload de um arquivo CSV com as colunas: <strong>COMPETÊNCIA (DATA DO FECHAMENTO)</strong>, <strong>NOME CLIENTE</strong>, <strong>CPF</strong>, <strong>BANCO</strong> (obrigatórios).
                Opcionais: <strong>N° CONTRATO</strong> (se ausente ou vazio, importa em branco), TIPO, N° PROCESSO, SITUAÇÃO, ADVOGADO DA INICIAL, OBSERVAÇÃO DO ATENDIMENTO, OBSERVAÇÃO PROTOCOLO DO PROCESSO, ESTADO, CONSULTOR DE VENDAS.
                Use ponto e vírgula (;) como delimitador. O cliente deve estar cadastrado antes.
              </Typography>
            )}

            {importMode === "processos" && (
              <>
                <Box sx={{ mb: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={handleDownloadTemplate}
                    startIcon={<IconDownload />}
                    sx={{ mr: 2 }}
                  >
                    Baixar Template
                  </Button>
                  <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 1 }}>
                    Baixe o template para verificar o formato correto do arquivo
                  </Typography>
                </Box>

                <Divider sx={{ my: 2 }} />
              </>
            )}

            {importMode === "processos" && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={deleteImportedBefore}
                    onChange={(e) => setDeleteImportedBefore(e.target.checked)}
                    color="primary"
                  />
                }
                label="Excluir contratos importados anteriormente antes de importar"
                sx={{ mb: 2, display: "block" }}
              />
            )}

            <input
              type="file"
              accept=".csv"
              onChange={handleFileSelect}
              ref={fileInputRef}
              style={{ display: "none" }}
            />

            <Button
              variant="outlined"
              onClick={() => fileInputRef.current?.click()}
              startIcon={<IconUpload />}
              sx={{ mb: 2 }}
            >
              Selecionar Arquivo CSV
            </Button>

            {selectedFile && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2">
                  Arquivo selecionado: <strong>{selectedFile.name}</strong>
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Tamanho: {(selectedFile.size / 1024).toFixed(2)} KB
                </Typography>
              </Box>
            )}

            {importing && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Importando arquivo...
                </Typography>
                <LinearProgress />
              </Box>
            )}

            {importResult && (
              <Box sx={{ mt: 2 }}>
                {importResult.success ? (
                  <Alert severity={importResult.total_errors > 0 ? "warning" : "success"}>
                    <Typography variant="body2">
                      <strong>Importação concluída!</strong>
                    </Typography>
                    <Typography variant="body2">
                      • Criados: {importResult.created ?? 0} contratos
                    </Typography>
                    {(importResult.updated ?? 0) > 0 && (
                      <Typography variant="body2">
                        • Atualizados: {importResult.updated} contratos
                      </Typography>
                    )}
                    {importResult.deleted > 0 && (
                      <Typography variant="body2">
                        • Excluídos anteriormente: {importResult.deleted} contratos
                      </Typography>
                    )}
                    {importResult.file_at_updated > 0 && (
                      <Typography variant="body2">
                        • Datas "ajuizar em" redistribuídas: {importResult.file_at_updated} contratos
                      </Typography>
                    )}
                    {importResult.skipped > 0 && (
                      <Typography variant="body2">
                        • Ignorados: {importResult.skipped} linhas
                      </Typography>
                    )}
                    {importResult.total_errors > 0 && (
                      <Typography variant="body2">
                        • Erros: {importResult.total_errors} linhas
                      </Typography>
                    )}
                  </Alert>
                ) : (
                  <Alert severity="error">
                    <Typography variant="body2">
                      <strong>Erro ao importar o arquivo:</strong>
                    </Typography>
                    <Typography variant="body2">{importResult.error}</Typography>
                    {importResult.found_headers && (
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="caption" display="block">
                          <strong>Cabeçalhos encontrados:</strong>{" "}
                          {importResult.found_headers.join(", ")}
                        </Typography>
                      </Box>
                    )}
                  </Alert>
                )}

                {importResult.errors && importResult.errors.length > 0 && (
                  <Box sx={{ mt: 2, maxHeight: 200, overflow: "auto" }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Detalhes dos erros encontrados:</strong>
                    </Typography>
                    {importResult.errors.map((error, index) => (
                      <Typography key={index} variant="caption" color="error" display="block">
                        {error}
                      </Typography>
                    ))}
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseImportModal} color="secondary">
            Fechar
          </Button>
          <Button
            onClick={handleImportCSV}
            color="primary"
            disabled={!selectedFile || importing}
            startIcon={importing ? null : <IconUpload />}
          >
            {importing ? "Importando..." : "Importar"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ContractsList;
